import React from 'react';
import { Box, Card, CardContent, Typography, alpha } from '@mui/material';
import { CheckCircle, Cancel, Warning, Lock, LockOpen, TrendingUp, TrendingDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const TradingGates = ({ canEnter, marketGate, noTrade, regime, downRisk20, downRisk60 }) => {
  const { t } = useTranslation();
  const getGateStatus = (isOpen) => ({
    icon: isOpen ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />,
    color: isOpen ? '#10b981' : '#ef4444',
    labelKey: isOpen ? 'risk.open' : 'risk.closed',
  });

  const marketStatus = getGateStatus(marketGate);
  const tradeStatus = getGateStatus(!noTrade);
  const overallStatus = getGateStatus(canEnter);

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2,
        border: '1.5px solid',
        borderColor: canEnter ? alpha('#10b981', 0.25) : alpha('#ef4444', 0.25),
        background: canEnter ? `linear-gradient(135deg, ${alpha('#10b981', 0.02)} 0%, #ffffff 100%)` : `linear-gradient(135deg, ${alpha('#ef4444', 0.02)} 0%, #ffffff 100%)`,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header - Ultra Compact */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
            {t('risk.tradingGates')}
          </Typography>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(overallStatus.color, 0.12),
              color: overallStatus.color,
            }}
          >
            {canEnter ? <LockOpen sx={{ fontSize: 18 }} /> : <Lock sx={{ fontSize: 18 }} />}
          </Box>
        </Box>

        {/* Overall Status - Text-based */}
        <Box
          sx={{
            pb: 1.5,
            mb: 1.5,
            borderBottom: '2px solid',
            borderColor: overallStatus.color,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', fontWeight: 500 }}>
              {t('risk.entryStatus')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {overallStatus.icon}
              <Typography variant="h6" sx={{ fontWeight: 800, color: overallStatus.color, fontSize: '0.875rem', letterSpacing: 0.5 }}>
                {canEnter ? t('risk.allowed') : t('risk.blocked')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Individual Gates - Text-based Professional */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Market Gate */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {marketStatus.icon}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.primary' }}>
                  {t('risk.marketGate')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 800, color: marketStatus.color, fontSize: '0.8125rem' }}>
                {t(marketStatus.labelKey)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', lineHeight: 1.2, pl: 2.5 }}>
              DR20: {((downRisk20 || 0) * 100).toFixed(1)}% | DR60: {((downRisk60 || 0) * 100).toFixed(1)}%
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ height: '1px', bgcolor: 'divider' }} />

          {/* Trade Zone */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {noTrade ? <Warning sx={{ fontSize: 14, color: tradeStatus.color }} /> : <CheckCircle sx={{ fontSize: 14, color: tradeStatus.color }} />}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.primary' }}>
                  {t('risk.tradeZone')}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 800, color: tradeStatus.color, fontSize: '0.8125rem' }}>
                {noTrade ? t('risk.noTrade') : t('common.active')}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', lineHeight: 1.2, pl: 2.5 }}>
              {noTrade ? t('risk.highUncertainty') : t('market.regime.riskOnDescription')}
            </Typography>
          </Box>

          {/* Divider */}
          <Box sx={{ height: '1px', bgcolor: 'divider' }} />

          {/* Regime */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {regime === 'RISK_ON' ? 
                  <TrendingUp sx={{ fontSize: 14, color: '#10b981' }} /> : 
                  <TrendingDown sx={{ fontSize: 14, color: '#ef4444' }} />
                }
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.primary' }}>
                  {t('risk.regime')}
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 800, 
                  color: regime === 'RISK_ON' ? '#10b981' : '#ef4444', 
                  fontSize: '0.8125rem',
                  letterSpacing: 0.5,
                }}
              >
                {regime === 'RISK_ON' ? t('risk.riskOn') : regime === 'RISK_OFF' ? t('risk.riskOff') : 'N/A'}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem', lineHeight: 1.2, pl: 2.5 }}>
              {regime === 'RISK_ON' ? t('market.regime.riskOnDescription') : t('risk.defensive')}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TradingGates;
